"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { Fragment, useEffect, useEffectEvent, useRef, useState } from "react";
import { Accordion, Col, Container, Form, FormCheck, Row, Table } from "react-bootstrap";
import { useChainId, useEnsName, useReadContract, useReadContracts, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import styles from "./Delegation.module.scss";
import { DELEGATION_ABI } from "@/abis/abis";
import { DELEGATION_ALL_ADDRESS, DELEGATION_CONTRACT, NULL_ADDRESS } from "@/constants/constants";
import { areEqualAddresses, getTransactionLink } from "@/helpers/Helpers";
import { DelegationCenterSection } from "@/types/enums";
import { faCircleArrowLeft, faEdit, faInfoCircle, faLock, faLockOpen, faMinus, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "react-tooltip";
import { useSeizeConnectContext } from "../auth/SeizeConnectContext";
import { Spinner } from "../dotLoader/DotLoader";
import { getDelegationsFromData, getParams, getReadParams, type ContractDelegation, type ContractWalletDelegation } from "./CollectionDelegation.utils";
import type { DelegationCollection } from "./delegation-constants";
import { ALL_USE_CASES, ANY_COLLECTION_PATH, CONSOLIDATION_USE_CASE, DELEGATION_USE_CASES, MAX_BULK_ACTIONS, MEMES_COLLECTION, PRIMARY_ADDRESS_USE_CASE, SUB_DELEGATION_USE_CASE } from "./delegation-constants";
import { DelegationToast } from "./DelegationCenterMenu";
import DelegationWallet from "./DelegationWallet";
import NewAssignPrimaryAddress from "./NewAssignPrimaryAddress";
import NewConsolidationComponent from "./NewConsolidation";
import NewDelegationComponent from "./NewDelegation";
import NewSubDelegationComponent from "./NewSubDelegation";
import RevokeDelegationWithSubComponent from "./RevokeDelegationWithSub";
import UpdateDelegationComponent from "./UpdateDelegation";
interface Props {
  setSection(section: DelegationCenterSection): any;
  collection: DelegationCollection;
}
interface Revocation {
  use_case: number;
  wallet: string;
}
function getActiveDelegationsReadParams(address: `0x${string}` | string | undefined, collection: `0x${string}` | string | undefined, functionName: string) {
  return getParams(address, collection, functionName, DELEGATION_USE_CASES);
}
function getConsolidationReadParams(address: `0x${string}` | string | undefined, collection: `0x${string}` | string | undefined, consolidationAddresses: ContractDelegation) {
  const params: any = [];
  if (consolidationAddresses) {
    consolidationAddresses.wallets.map(ca => params.push({
      address: DELEGATION_CONTRACT.contract,
      abi: DELEGATION_ABI,
      chainId: DELEGATION_CONTRACT.chain_id,
      functionName: "checkConsolidationStatus",
      args: [address, ca.wallet, collection]
    }));
    return params;
  }
  return [];
}
function getDelegationsCount(delegations: ContractDelegation[]) {
  let count = 0;
  for (const delegation of delegations) {
    if (delegation.wallets.length > 0) {
      count += delegation.wallets.length;
    }
  }
  return count;
}
function getActiveKeys(outDelegations: ContractDelegation[], inDelegations: ContractDelegation[]) {
  const outCount = getDelegationsCount(outDelegations);
  const inCount = getDelegationsCount(inDelegations);
  if (outCount > 0 && inCount > 0) {
    return ["0", "1"];
  }
  if (outCount > 0) {
    return ["0"];
  }
  if (inCount > 0) {
    return ["1"];
  }
  return [""];
}
export default function CollectionDelegationComponent(props) {
  const $ = _c(231);
  const toastRef = useRef(null);
  const accountResolution = useSeizeConnectContext();
  const networkResolution = useChainId();
  const t0 = accountResolution.address as `0x${string}`;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      address: t0,
      chainId: 1
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ensResolution = useEnsName(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [outgoingDelegations, setOutgoingDelegations] = useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [outgoingActiveConsolidations, setOutgoingActiveConsolidations] = useState(t3);
  const [outgoingDelegationsLoaded, setOutgoingDelegationsLoaded] = useState(false);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [incomingDelegations, setIncomingDelegations] = useState(t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [incomingActiveConsolidations, setIncomingActiveConsolidations] = useState(t5);
  const [incomingDelegationsLoaded, setIncomingDelegationsLoaded] = useState(false);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const [bulkRevocations, setBulkRevocations] = useState(t6);
  const [showUpdateDelegation, setShowUpdateDelegation] = useState(false);
  const [updateDelegationParams, setUpdateDelegationParams] = useState();
  const [lockUseCaseValue, setLockUseCaseValue] = useState(0);
  const [lockUseCaseIndex, setLockUseCaseIndex] = useState(0);
  const [subDelegationOriginalDelegator, setSubDelegationOriginalDelegator] = useState(undefined);
  const [showCreateNewDelegationWithSub, setShowCreateNewDelegationWithSub] = useState(false);
  const [showCreateNewSubDelegationWithSub, setShowCreateNewSubDelegationWithSub] = useState(false);
  const [showCreateNewConsolidationWithSub, setShowCreateNewConsolidationWithSub] = useState(false);
  const [showAssignPrimaryAddressWithSub, setShowAssignPrimaryAddressWithSub] = useState(false);
  const [showRevokeDelegationWithSub, setShowRevokeDelegationWithSub] = useState(false);
  let t7;
  if ($[7] !== networkResolution) {
    t7 = function chainsMatch() {
      return networkResolution === DELEGATION_CONTRACT.chain_id;
    };
    $[7] = networkResolution;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const chainsMatch = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = function getSwitchToHtml() {
      return `<span style="color: red !important;">Switch to ${DELEGATION_CONTRACT.chain_id === 1 ? "Ethereum Mainnet" : "Sepolia Network"}</span>`;
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const getSwitchToHtml = t8;
  const t9 = accountResolution.address as `0x${string}`;
  let t10;
  if ($[10] !== props.collection.contract || $[11] !== t9) {
    t10 = getActiveDelegationsReadParams(t9, props.collection.contract, "retrieveDelegationAddressesTokensIDsandExpiredDates");
    $[10] = props.collection.contract;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== accountResolution.isConnected) {
    t11 = {
      enabled: accountResolution.isConnected,
      refetchInterval: 10000
    };
    $[13] = accountResolution.isConnected;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11) {
    t12 = {
      contracts: t10,
      query: t11
    };
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const retrieveOutgoingDelegations = useReadContracts(t12);
  let t13;
  let t14;
  if ($[18] !== retrieveOutgoingDelegations.data) {
    t13 = () => {
      if (retrieveOutgoingDelegations.data) {
        const myDelegations = getDelegationsFromData(retrieveOutgoingDelegations.data);
        setOutgoingDelegations(myDelegations);
        setOutgoingDelegationsLoaded(true);
      }
    };
    t14 = [retrieveOutgoingDelegations.data];
    $[18] = retrieveOutgoingDelegations.data;
    $[19] = t13;
    $[20] = t14;
  } else {
    t13 = $[19];
    t14 = $[20];
  }
  useEffect(t13, t14);
  const t15 = accountResolution.address as `0x${string}`;
  const t16 = outgoingDelegations[CONSOLIDATION_USE_CASE.index];
  let t17;
  if ($[21] !== props.collection.contract || $[22] !== t15 || $[23] !== t16) {
    t17 = getConsolidationReadParams(t15, props.collection.contract, t16);
    $[21] = props.collection.contract;
    $[22] = t15;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const t18 = accountResolution.isConnected && outgoingDelegations.length > 0;
  let t19;
  if ($[25] !== t18) {
    t19 = {
      enabled: t18,
      refetchInterval: 10000
    };
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t17 || $[28] !== t19) {
    t20 = {
      contracts: t17,
      query: t19
    };
    $[27] = t17;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  const retrieveOutgoingConsolidations = useReadContracts(t20);
  let t21;
  let t22;
  if ($[30] !== outgoingDelegations || $[31] !== retrieveOutgoingConsolidations.data) {
    t21 = () => {
      if (!retrieveOutgoingConsolidations.data) {
        return;
      }
      const consolidationDelegations = outgoingDelegations[CONSOLIDATION_USE_CASE.index];
      if (!consolidationDelegations?.wallets.length) {
        setOutgoingActiveConsolidations([]);
        return;
      }
      const activeConsolidations = consolidationDelegations.wallets.map((walletDelegation, index) => ({
        wallet: walletDelegation.wallet,
        status: retrieveOutgoingConsolidations.data?.[index]?.result ? "consolidation active" : "consolidation incomplete"
      }));
      setOutgoingActiveConsolidations(activeConsolidations);
    };
    t22 = [outgoingDelegations, retrieveOutgoingConsolidations.data];
    $[30] = outgoingDelegations;
    $[31] = retrieveOutgoingConsolidations.data;
    $[32] = t21;
    $[33] = t22;
  } else {
    t21 = $[32];
    t22 = $[33];
  }
  useEffect(t21, t22);
  const t23 = accountResolution.address as `0x${string}`;
  let t24;
  if ($[34] !== props.collection.contract || $[35] !== t23) {
    t24 = getActiveDelegationsReadParams(t23, props.collection.contract, "retrieveDelegatorsTokensIDsandExpiredDates");
    $[34] = props.collection.contract;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] !== accountResolution.isConnected) {
    t25 = {
      enabled: accountResolution.isConnected,
      refetchInterval: 10000
    };
    $[37] = accountResolution.isConnected;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== t24 || $[40] !== t25) {
    t26 = {
      contracts: t24,
      query: t25
    };
    $[39] = t24;
    $[40] = t25;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  const retrieveIncomingDelegations = useReadContracts(t26);
  let t27;
  let t28;
  if ($[42] !== retrieveIncomingDelegations.data) {
    t27 = () => {
      if (retrieveIncomingDelegations.data) {
        const myDelegations_0 = getDelegationsFromData(retrieveIncomingDelegations.data);
        setIncomingDelegations(myDelegations_0);
        setIncomingDelegationsLoaded(true);
      }
    };
    t28 = [retrieveIncomingDelegations.data];
    $[42] = retrieveIncomingDelegations.data;
    $[43] = t27;
    $[44] = t28;
  } else {
    t27 = $[43];
    t28 = $[44];
  }
  useEffect(t27, t28);
  const t29 = accountResolution.address as `0x${string}`;
  const t30 = incomingDelegations[CONSOLIDATION_USE_CASE.index];
  let t31;
  if ($[45] !== props.collection.contract || $[46] !== t29 || $[47] !== t30) {
    t31 = getConsolidationReadParams(t29, props.collection.contract, t30);
    $[45] = props.collection.contract;
    $[46] = t29;
    $[47] = t30;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  const t32 = accountResolution.isConnected && incomingDelegations.length > 0;
  let t33;
  if ($[49] !== t32) {
    t33 = {
      enabled: t32,
      refetchInterval: 10000
    };
    $[49] = t32;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== t31 || $[52] !== t33) {
    t34 = {
      contracts: t31,
      query: t33
    };
    $[51] = t31;
    $[52] = t33;
    $[53] = t34;
  } else {
    t34 = $[53];
  }
  const retrieveIncomingConsolidations = useReadContracts(t34);
  let t35;
  let t36;
  if ($[54] !== incomingDelegations || $[55] !== retrieveIncomingConsolidations.data) {
    t35 = () => {
      if (!retrieveIncomingConsolidations.data) {
        return;
      }
      const consolidationDelegations_0 = incomingDelegations[CONSOLIDATION_USE_CASE.index];
      if (!consolidationDelegations_0?.wallets.length) {
        setIncomingActiveConsolidations([]);
        return;
      }
      const activeConsolidations_0 = consolidationDelegations_0.wallets.map((walletDelegation_0, index_0) => ({
        wallet: walletDelegation_0.wallet,
        status: retrieveIncomingConsolidations.data?.[index_0]?.result ? "consolidation active" : "consolidation incomplete"
      }));
      setIncomingActiveConsolidations(activeConsolidations_0);
    };
    t36 = [incomingDelegations, retrieveIncomingConsolidations.data];
    $[54] = incomingDelegations;
    $[55] = retrieveIncomingConsolidations.data;
    $[56] = t35;
    $[57] = t36;
  } else {
    t35 = $[56];
    t36 = $[57];
  }
  useEffect(t35, t36);
  let t37;
  if ($[58] !== accountResolution.address || $[59] !== accountResolution.isConnected || $[60] !== props.collection.contract) {
    t37 = areEqualAddresses(props.collection.contract, DELEGATION_ALL_ADDRESS) ? {} : {
      contracts: getReadParams(DELEGATION_ALL_ADDRESS, accountResolution.address as `0x${string}`, "retrieveCollectionUseCaseLockStatus"),
      query: {
        enabled: accountResolution.isConnected,
        refetchInterval: 10000
      }
    };
    $[58] = accountResolution.address;
    $[59] = accountResolution.isConnected;
    $[60] = props.collection.contract;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  const useCaseLockStatusesGlobalParams = t37;
  const useCaseLockStatusesGlobal = useReadContracts(useCaseLockStatusesGlobalParams);
  const t38 = accountResolution.address as `0x${string}`;
  let t39;
  if ($[62] !== props.collection.contract || $[63] !== t38) {
    t39 = getReadParams(props.collection.contract, t38, "retrieveCollectionUseCaseLockStatus", ALL_USE_CASES);
    $[62] = props.collection.contract;
    $[63] = t38;
    $[64] = t39;
  } else {
    t39 = $[64];
  }
  let t40;
  if ($[65] !== accountResolution.isConnected) {
    t40 = {
      enabled: accountResolution.isConnected,
      refetchInterval: 10000
    };
    $[65] = accountResolution.isConnected;
    $[66] = t40;
  } else {
    t40 = $[66];
  }
  let t41;
  if ($[67] !== t39 || $[68] !== t40) {
    t41 = {
      contracts: t39,
      query: t40
    };
    $[67] = t39;
    $[68] = t40;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  const useCaseLockStatuses = useReadContracts(t41);
  const {
    refetch: refetchUseCaseLockStatuses
  } = useCaseLockStatuses;
  const [revokeDelegationParams, setRevokeDelegationParams] = useState();
  const [batchRevokeDelegationParams, setBatchRevokeDelegationParams] = useState();
  const contractWriteRevoke = useWriteContract();
  let t42;
  if ($[70] !== contractWriteRevoke.data) {
    t42 = {
      confirmations: 1,
      hash: contractWriteRevoke.data
    };
    $[70] = contractWriteRevoke.data;
    $[71] = t42;
  } else {
    t42 = $[71];
  }
  const waitContractWriteRevoke = useWaitForTransactionReceipt(t42);
  const contractWriteBatchRevoke = useWriteContract();
  let t43;
  if ($[72] !== contractWriteBatchRevoke.data) {
    t43 = {
      confirmations: 1,
      hash: contractWriteBatchRevoke.data
    };
    $[72] = contractWriteBatchRevoke.data;
    $[73] = t43;
  } else {
    t43 = $[73];
  }
  const waitContractWriteBatchRevoke = useWaitForTransactionReceipt(t43);
  const [toast, setToast] = useState(undefined);
  const [showToast, setShowToast] = useState(false);
  let t44;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = [];
    $[74] = t44;
  } else {
    t44 = $[74];
  }
  const [delegationKeys, setDelegationKeys] = useState(t44);
  const [delegationKeysChanged, setDelegationKeysChanged] = useState(false);
  let t45;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = [];
    $[75] = t45;
  } else {
    t45 = $[75];
  }
  const [subDelegationKeys, setSubDelegationKeys] = useState(t45);
  const [subDelegationKeysChanged, setSubDelegationKeysChanged] = useState(false);
  let t46;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = [];
    $[76] = t46;
  } else {
    t46 = $[76];
  }
  const [consolidationKeys, setConsolidationKeys] = useState(t46);
  const [consolidationKeysChanged, setConsolidationKeysChanged] = useState(false);
  let t47;
  if ($[77] !== accountResolution.address || $[78] !== accountResolution.isConnected || $[79] !== props.collection.contract) {
    t47 = areEqualAddresses(props.collection.contract, DELEGATION_ALL_ADDRESS) ? {} : {
      address: DELEGATION_CONTRACT.contract,
      abi: DELEGATION_ABI,
      chainId: DELEGATION_CONTRACT.chain_id,
      functionName: "retrieveCollectionLockStatus",
      args: [DELEGATION_ALL_ADDRESS, accountResolution.address],
      query: {
        enabled: accountResolution.isConnected,
        refetchInterval: 10000
      }
    };
    $[77] = accountResolution.address;
    $[78] = accountResolution.isConnected;
    $[79] = props.collection.contract;
    $[80] = t47;
  } else {
    t47 = $[80];
  }
  const collectionLockReadGlobalParams = t47;
  const collectionLockReadGlobal = useReadContract(collectionLockReadGlobalParams);
  let t48;
  if ($[81] !== accountResolution.address || $[82] !== props.collection.contract) {
    t48 = [props.collection.contract, accountResolution.address];
    $[81] = accountResolution.address;
    $[82] = props.collection.contract;
    $[83] = t48;
  } else {
    t48 = $[83];
  }
  let t49;
  if ($[84] !== accountResolution.isConnected) {
    t49 = {
      enabled: accountResolution.isConnected,
      refetchInterval: 10000
    };
    $[84] = accountResolution.isConnected;
    $[85] = t49;
  } else {
    t49 = $[85];
  }
  let t50;
  if ($[86] !== t48 || $[87] !== t49) {
    t50 = {
      address: DELEGATION_CONTRACT.contract,
      abi: DELEGATION_ABI,
      chainId: DELEGATION_CONTRACT.chain_id,
      functionName: "retrieveCollectionLockStatus",
      args: t48,
      query: t49
    };
    $[86] = t48;
    $[87] = t49;
    $[88] = t50;
  } else {
    t50 = $[88];
  }
  const collectionLockRead = useReadContract(t50);
  const collectionLockWrite = useWriteContract();
  let t51;
  if ($[89] !== collectionLockWrite.data) {
    t51 = {
      confirmations: 1,
      hash: collectionLockWrite.data
    };
    $[89] = collectionLockWrite.data;
    $[90] = t51;
  } else {
    t51 = $[90];
  }
  const waitCollectionLockWrite = useWaitForTransactionReceipt(t51);
  const useCaseLockWrite = useWriteContract();
  let t52;
  if ($[91] !== useCaseLockWrite.data) {
    t52 = {
      confirmations: 1,
      hash: useCaseLockWrite.data
    };
    $[91] = useCaseLockWrite.data;
    $[92] = t52;
  } else {
    t52 = $[92];
  }
  const waitUseCaseLockWrite = useWaitForTransactionReceipt(t52);
  let t53;
  if ($[93] !== accountResolution.isConnected || $[94] !== refetchUseCaseLockStatuses) {
    t53 = () => {
      if (accountResolution.isConnected) {
        refetchUseCaseLockStatuses();
      }
    };
    $[93] = accountResolution.isConnected;
    $[94] = refetchUseCaseLockStatuses;
    $[95] = t53;
  } else {
    t53 = $[95];
  }
  let t54;
  if ($[96] !== accountResolution.isConnected || $[97] !== refetchUseCaseLockStatuses || $[98] !== waitUseCaseLockWrite.isSuccess) {
    t54 = [accountResolution.isConnected, refetchUseCaseLockStatuses, waitUseCaseLockWrite.isSuccess];
    $[96] = accountResolution.isConnected;
    $[97] = refetchUseCaseLockStatuses;
    $[98] = waitUseCaseLockWrite.isSuccess;
    $[99] = t54;
  } else {
    t54 = $[99];
  }
  useEffect(t53, t54);
  let t55;
  let t56;
  if ($[100] !== contractWriteRevoke.data || $[101] !== contractWriteRevoke.error || $[102] !== waitContractWriteRevoke.isLoading) {
    t55 = () => {
      if (contractWriteRevoke.error) {
        setToast({
          title: "Revoking Delegation Failed",
          message: contractWriteRevoke.error.message.split("Request Arguments")[0]
        });
      }
      if (contractWriteRevoke.data) {
        if (contractWriteRevoke.data) {
          if (waitContractWriteRevoke.isLoading) {
            setToast({
              title: "Revoking Delegation",
              message: `Transaction submitted...
                    <a
                    href=${getTransactionLink(DELEGATION_CONTRACT.chain_id, contractWriteRevoke.data)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=${styles.etherscanLink}>
                    view
                  </a><br />Waiting for confirmation...`
            });
          } else {
            setToast({
              title: "Revoking Delegation",
              message: `Transaction Successful!
                    <a
                    href=${getTransactionLink(DELEGATION_CONTRACT.chain_id, contractWriteRevoke.data)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=${styles.etherscanLink}>
                    view
                  </a>`
            });
          }
        }
      }
    };
    t56 = [contractWriteRevoke.error, contractWriteRevoke.data, waitContractWriteRevoke.isLoading];
    $[100] = contractWriteRevoke.data;
    $[101] = contractWriteRevoke.error;
    $[102] = waitContractWriteRevoke.isLoading;
    $[103] = t55;
    $[104] = t56;
  } else {
    t55 = $[103];
    t56 = $[104];
  }
  useEffect(t55, t56);
  let t57;
  let t58;
  if ($[105] !== contractWriteBatchRevoke.data || $[106] !== contractWriteBatchRevoke.error || $[107] !== waitContractWriteBatchRevoke.isLoading) {
    t57 = () => {
      if (contractWriteBatchRevoke.error) {
        setToast({
          title: "Revoking Delegations Failed",
          message: contractWriteBatchRevoke.error.message.split("Request Arguments")[0]
        });
      }
      if (contractWriteBatchRevoke.data) {
        if (contractWriteBatchRevoke.data) {
          if (waitContractWriteBatchRevoke.isLoading) {
            setToast({
              title: "Batch Revoking Delegations",
              message: `Transaction submitted...
                    <a
                    href=${getTransactionLink(DELEGATION_CONTRACT.chain_id, contractWriteBatchRevoke.data)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=${styles.etherscanLink}>
                    view
                  </a><br />Waiting for confirmation...`
            });
          } else {
            setBulkRevocations([]);
            setToast({
              title: "Batch Revoking Delegations",
              message: `Transaction Successful!
                    <a
                    href=${getTransactionLink(DELEGATION_CONTRACT.chain_id, contractWriteBatchRevoke.data)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=${styles.etherscanLink}>
                    view
                  </a>`
            });
          }
        }
      }
    };
    t58 = [contractWriteBatchRevoke.error, contractWriteBatchRevoke.data, waitContractWriteBatchRevoke.isLoading];
    $[105] = contractWriteBatchRevoke.data;
    $[106] = contractWriteBatchRevoke.error;
    $[107] = waitContractWriteBatchRevoke.isLoading;
    $[108] = t57;
    $[109] = t58;
  } else {
    t57 = $[108];
    t58 = $[109];
  }
  useEffect(t57, t58);
  let t59;
  let t60;
  if ($[110] !== collectionLockRead.data || $[111] !== collectionLockWrite.data || $[112] !== collectionLockWrite.error || $[113] !== waitCollectionLockWrite.isLoading) {
    t59 = () => {
      if (collectionLockWrite.error) {
        const title = `${collectionLockRead.data ? "Unlocking" : "Locking"} Wallet`;
        setToast({
          title,
          message: collectionLockWrite.error.message.split("Request Arguments")[0]
        });
      }
      if (collectionLockWrite.data) {
        if (collectionLockWrite.data) {
          if (waitCollectionLockWrite.isLoading) {
            setToast({
              title: "Locking Wallet",
              message: `Transaction submitted...
                    <a
                    href=${getTransactionLink(DELEGATION_CONTRACT.chain_id, collectionLockWrite.data)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=${styles.etherscanLink}>
                    view
                  </a><br />Waiting for confirmation...`
            });
          } else {
            setToast({
              title: "Locking Wallet",
              message: `Transaction Successful!
                    <a
                    href=${getTransactionLink(DELEGATION_CONTRACT.chain_id, collectionLockWrite.data)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=${styles.etherscanLink}>
                    view
                  </a>`
            });
          }
        }
      }
    };
    t60 = [collectionLockWrite.error, collectionLockWrite.data, collectionLockRead.data, waitCollectionLockWrite.isLoading];
    $[110] = collectionLockRead.data;
    $[111] = collectionLockWrite.data;
    $[112] = collectionLockWrite.error;
    $[113] = waitCollectionLockWrite.isLoading;
    $[114] = t59;
    $[115] = t60;
  } else {
    t59 = $[114];
    t60 = $[115];
  }
  useEffect(t59, t60);
  let t61;
  let t62;
  if ($[116] !== lockUseCaseIndex || $[117] !== useCaseLockStatuses.data || $[118] !== useCaseLockWrite.data || $[119] !== useCaseLockWrite.error || $[120] !== waitUseCaseLockWrite.isLoading) {
    t61 = () => {
      const useCase = DELEGATION_USE_CASES[lockUseCaseIndex];
      const title_0 = `${useCaseLockStatuses.data?.[lockUseCaseIndex] ? "Unlocking" : "Locking"} Wallet on Use Case #${useCase?.use_case} - ${useCase?.display}`;
      if (useCaseLockWrite.error) {
        setToast({
          title: title_0,
          message: useCaseLockWrite.error.message.split("Request Arguments")[0]
        });
      }
      if (useCaseLockWrite.data) {
        if (useCaseLockWrite.data) {
          if (waitUseCaseLockWrite.isLoading) {
            setToast({
              title: title_0,
              message: `Transaction submitted...
                    <a
                    href=${getTransactionLink(DELEGATION_CONTRACT.chain_id, useCaseLockWrite.data)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=${styles.etherscanLink}>
                    view
                  </a><br />Waiting for confirmation...`
            });
          } else {
            setToast({
              title: title_0,
              message: `Transaction Successful!
                    <a
                    href=${getTransactionLink(DELEGATION_CONTRACT.chain_id, useCaseLockWrite.data)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=${styles.etherscanLink}>
                    view
                  </a>`
            });
          }
        }
      }
    };
    t62 = [useCaseLockWrite.error, useCaseLockWrite.data, lockUseCaseIndex, useCaseLockStatuses.data, waitUseCaseLockWrite.isLoading];
    $[116] = lockUseCaseIndex;
    $[117] = useCaseLockStatuses.data;
    $[118] = useCaseLockWrite.data;
    $[119] = useCaseLockWrite.error;
    $[120] = waitUseCaseLockWrite.isLoading;
    $[121] = t61;
    $[122] = t62;
  } else {
    t61 = $[121];
    t62 = $[122];
  }
  useEffect(t61, t62);
  let t63;
  let t64;
  if ($[123] !== incomingDelegationsLoaded || $[124] !== outgoingDelegationsLoaded || $[125] !== showToast) {
    t63 = () => {
      if (!showToast && outgoingDelegationsLoaded && incomingDelegationsLoaded) {
        setToast(undefined);
      }
    };
    t64 = [incomingDelegationsLoaded, outgoingDelegationsLoaded, showToast];
    $[123] = incomingDelegationsLoaded;
    $[124] = outgoingDelegationsLoaded;
    $[125] = showToast;
    $[126] = t63;
    $[127] = t64;
  } else {
    t63 = $[126];
    t64 = $[127];
  }
  useEffect(t63, t64);
  let t65;
  let t66;
  if ($[128] !== toast) {
    t65 = () => {
      if (toast) {
        setShowToast(true);
      }
    };
    t66 = [toast];
    $[128] = toast;
    $[129] = t65;
    $[130] = t66;
  } else {
    t65 = $[129];
    t66 = $[130];
  }
  useEffect(t65, t66);
  let t67;
  let t68;
  if ($[131] !== contractWriteRevoke || $[132] !== revokeDelegationParams) {
    t67 = () => {
      if (revokeDelegationParams && !revokeDelegationParams.loading) {
        setRevokeDelegationParams({
          ...revokeDelegationParams,
          loading: true
        });
        contractWriteRevoke.writeContract({
          address: DELEGATION_CONTRACT.contract,
          abi: DELEGATION_ABI,
          chainId: DELEGATION_CONTRACT.chain_id,
          args: [revokeDelegationParams ? revokeDelegationParams.collection : NULL_ADDRESS, revokeDelegationParams ? revokeDelegationParams.address : NULL_ADDRESS, revokeDelegationParams ? revokeDelegationParams.use_case : 0],
          functionName: "revokeDelegationAddress"
        });
      }
    };
    t68 = [contractWriteRevoke, revokeDelegationParams];
    $[131] = contractWriteRevoke;
    $[132] = revokeDelegationParams;
    $[133] = t67;
    $[134] = t68;
  } else {
    t67 = $[133];
    t68 = $[134];
  }
  useEffect(t67, t68);
  let t69;
  let t70;
  if ($[135] !== batchRevokeDelegationParams || $[136] !== contractWriteBatchRevoke) {
    t69 = () => {
      if (batchRevokeDelegationParams && !batchRevokeDelegationParams.loading) {
        setBatchRevokeDelegationParams({
          ...batchRevokeDelegationParams,
          loading: true
        });
        contractWriteBatchRevoke.writeContract({
          address: DELEGATION_CONTRACT.contract,
          abi: DELEGATION_ABI,
          chainId: DELEGATION_CONTRACT.chain_id,
          args: [batchRevokeDelegationParams.collections, batchRevokeDelegationParams.addresses, batchRevokeDelegationParams.use_cases],
          functionName: "batchRevocations"
        });
      }
    };
    t70 = [batchRevokeDelegationParams, contractWriteBatchRevoke];
    $[135] = batchRevokeDelegationParams;
    $[136] = contractWriteBatchRevoke;
    $[137] = t69;
    $[138] = t70;
  } else {
    t69 = $[137];
    t70 = $[138];
  }
  useEffect(t69, t70);
  let t71;
  if ($[139] !== collectionLockWrite || $[140] !== contractWriteRevoke || $[141] !== retrieveIncomingDelegations || $[142] !== retrieveOutgoingDelegations || $[143] !== useCaseLockWrite) {
    t71 = () => {
      setOutgoingDelegations([]);
      setOutgoingDelegationsLoaded(false);
      retrieveOutgoingDelegations.refetch();
      setIncomingDelegations([]);
      setIncomingDelegationsLoaded(false);
      retrieveIncomingDelegations.refetch();
      setRevokeDelegationParams(undefined);
      setBatchRevokeDelegationParams(undefined);
      setToast(undefined);
      setLockUseCaseValue(0);
      setLockUseCaseIndex(0);
      useCaseLockWrite.reset();
      collectionLockWrite.reset();
      contractWriteRevoke.reset();
    };
    $[139] = collectionLockWrite;
    $[140] = contractWriteRevoke;
    $[141] = retrieveIncomingDelegations;
    $[142] = retrieveOutgoingDelegations;
    $[143] = useCaseLockWrite;
    $[144] = t71;
  } else {
    t71 = $[144];
  }
  const reset = useEffectEvent(t71);
  let t72;
  if ($[145] !== reset) {
    t72 = () => {
      reset();
    };
    $[145] = reset;
    $[146] = t72;
  } else {
    t72 = $[146];
  }
  let t73;
  if ($[147] !== accountResolution.address) {
    t73 = [accountResolution.address];
    $[147] = accountResolution.address;
    $[148] = t73;
  } else {
    t73 = $[148];
  }
  useEffect(t72, t73);
  let t74;
  let t75;
  if ($[149] !== consolidationKeysChanged || $[150] !== delegationKeysChanged || $[151] !== incomingDelegations || $[152] !== outgoingDelegations || $[153] !== subDelegationKeysChanged) {
    t74 = () => {
      const outDelegations = [...outgoingDelegations].filter(_temp);
      const inDelegations = [...incomingDelegations].filter(_temp2);
      if (!delegationKeysChanged) {
        setDelegationKeys(getActiveKeys(outDelegations, inDelegations));
      }
      if (!subDelegationKeysChanged) {
        setSubDelegationKeys(getActiveKeys([...outgoingDelegations].filter(_temp3), [...incomingDelegations].filter(_temp4)));
      }
      if (!consolidationKeysChanged) {
        setConsolidationKeys(getActiveKeys([...outgoingDelegations].filter(_temp5), [...incomingDelegations].filter(_temp6)));
      }
    };
    t75 = [incomingDelegations, outgoingDelegations, consolidationKeysChanged, delegationKeysChanged, subDelegationKeysChanged];
    $[149] = consolidationKeysChanged;
    $[150] = delegationKeysChanged;
    $[151] = incomingDelegations;
    $[152] = outgoingDelegations;
    $[153] = subDelegationKeysChanged;
    $[154] = t74;
    $[155] = t75;
  } else {
    t74 = $[154];
    t75 = $[155];
  }
  useEffect(t74, t75);
  let T0;
  let T1;
  let T2;
  let t76;
  let t77;
  if ($[156] !== accountResolution.address || $[157] !== bulkRevocations || $[158] !== chainsMatch || $[159] !== collectionLockRead.data || $[160] !== collectionLockReadGlobal?.data || $[161] !== collectionLockWrite || $[162] !== consolidationKeys || $[163] !== contractWriteBatchRevoke.isPending || $[164] !== delegationKeys || $[165] !== ensResolution || $[166] !== incomingActiveConsolidations || $[167] !== incomingDelegations || $[168] !== incomingDelegationsLoaded || $[169] !== lockUseCaseIndex || $[170] !== lockUseCaseValue || $[171] !== outgoingActiveConsolidations || $[172] !== outgoingDelegations || $[173] !== outgoingDelegationsLoaded || $[174] !== props || $[175] !== showAssignPrimaryAddressWithSub || $[176] !== showCreateNewConsolidationWithSub || $[177] !== showCreateNewDelegationWithSub || $[178] !== showCreateNewSubDelegationWithSub || $[179] !== showRevokeDelegationWithSub || $[180] !== showUpdateDelegation || $[181] !== subDelegationKeys || $[182] !== subDelegationOriginalDelegator || $[183] !== updateDelegationParams || $[184] !== useCaseLockStatuses.data || $[185] !== useCaseLockStatusesGlobal || $[186] !== useCaseLockWrite || $[187] !== waitCollectionLockWrite.isLoading || $[188] !== waitContractWriteBatchRevoke.isLoading || $[189] !== waitUseCaseLockWrite.isLoading) {
    const printDelegations = function printDelegations() {
      const outDelegations_0 = [...outgoingDelegations].filter(_temp7);
      const inDelegations_0 = [...incomingDelegations].filter(_temp8);
      return <><h5 className="pt-3 pb-1">Delegations</h5><Accordion alwaysOpen={true} className={styles.collectionDelegationsAccordion} activeKey={delegationKeys}><Accordion.Item className={`${styles.collectionDelegationsAccordionItem}`} eventKey="0"><Accordion.Header onClick={() => {
              if (delegationKeys.includes("0")) {
                setDelegationKeys(delegationKeys.filter(_temp9));
              } else {
                setDelegationKeys([...delegationKeys, "0"]);
              }
              setDelegationKeysChanged(true);
            }}>Outgoing</Accordion.Header><Accordion.Body>{printOutgoingDelegations("delegations", outDelegations_0)}</Accordion.Body></Accordion.Item><Accordion.Item className={`${styles.collectionDelegationsAccordionItem} mt-3`} eventKey="1"><Accordion.Header onClick={() => {
              if (delegationKeys.includes("1")) {
                setDelegationKeys(delegationKeys.filter(_temp10));
              } else {
                setDelegationKeys([...delegationKeys, "1"]);
              }
              setDelegationKeysChanged(true);
            }}>Incoming</Accordion.Header><Accordion.Body>{printIncomingDelegations("delegations", inDelegations_0)}</Accordion.Body></Accordion.Item></Accordion></>;
    };
    const printSubDelegations = function printSubDelegations() {
      return <><h5 className="pt-5 pb-1">Use A Delegation Manager (For Delegations or Consolidations)</h5><Accordion alwaysOpen={true} className={`${styles.collectionDelegationsAccordion} `} activeKey={subDelegationKeys}><Accordion.Item className={`${styles.collectionDelegationsAccordionItem}`} eventKey="0"><Accordion.Header onClick={() => {
              if (subDelegationKeys.includes("0")) {
                setSubDelegationKeys(subDelegationKeys.filter(_temp11));
              } else {
                setSubDelegationKeys([...subDelegationKeys, "0"]);
              }
              setSubDelegationKeysChanged(true);
            }}>Outgoing</Accordion.Header><Accordion.Body>{printOutgoingDelegations("Delegation Managers", [...outgoingDelegations].filter(_temp12))}</Accordion.Body></Accordion.Item><Accordion.Item className={`${styles.collectionDelegationsAccordionItem} mt-3`} eventKey="1"><Accordion.Header onClick={() => {
              if (subDelegationKeys.includes("1")) {
                setSubDelegationKeys(subDelegationKeys.filter(_temp13));
              } else {
                setSubDelegationKeys([...subDelegationKeys, "1"]);
              }
              setSubDelegationKeysChanged(true);
            }}>Incoming</Accordion.Header><Accordion.Body>{printIncomingDelegations("Delegation Managers", [...incomingDelegations].filter(_temp14), true)}</Accordion.Body></Accordion.Item></Accordion></>;
    };
    const printConsolidations = function printConsolidations() {
      return <><h5 className="pt-5 pb-1">Consolidations</h5><Accordion alwaysOpen={true} className={`${styles.collectionDelegationsAccordion}`} activeKey={consolidationKeys}><Accordion.Item className={`${styles.collectionDelegationsAccordionItem}`} eventKey="0"><Accordion.Header onClick={() => {
              if (consolidationKeys.includes("0")) {
                setConsolidationKeys(consolidationKeys.filter(_temp15));
              } else {
                setConsolidationKeys([...consolidationKeys, "0"]);
              }
              setConsolidationKeysChanged(true);
            }}>Outgoing</Accordion.Header><Accordion.Body>{printOutgoingDelegations("consolidations", [...outgoingDelegations].filter(_temp16))}</Accordion.Body></Accordion.Item><Accordion.Item className={`${styles.collectionDelegationsAccordionItem} mt-3`} eventKey="1"><Accordion.Header onClick={() => {
              if (consolidationKeys.includes("1")) {
                setConsolidationKeys(consolidationKeys.filter(_temp17));
              } else {
                setConsolidationKeys([...consolidationKeys, "1"]);
              }
              setConsolidationKeysChanged(true);
            }}>Incoming</Accordion.Header><Accordion.Body>{printIncomingDelegations("consolidations", [...incomingDelegations].filter(_temp18))}</Accordion.Body></Accordion.Item></Accordion></>;
    };
    let t78;
    if ($[195] === Symbol.for("react.memo_cache_sentinel")) {
      t78 = function printDelegationRowDetails(label, w, consolidationStatus, pending, isConsolidation) {
        return <span className="d-flex flex-column gap-1"><DelegationWallet address={w.wallet} /><span className="d-flex align-items-center gap-3"><span className="font-color-h">{w.all ? "all tokens" : ` - token ID: ${w.tokens}`}</span><span className={w.expiry === "expired" ? styles.delegationExpiredLabel : styles.delegationActiveLabel}>{w.expiry}</span>{isConsolidation && <span className={!pending ? styles.consolidationActiveLabel : styles.consolidationNotAcceptedLabel}>{consolidationStatus}{pending && <><FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} data-tooltip-id={`consolidation-missing-${label}`} /><Tooltip id={`consolidation-missing-${label}`} style={{
                  backgroundColor: "#1F2937",
                  color: "white",
                  padding: "4px 8px"
                }}>{label} consolidation missing</Tooltip></>}</span>}</span></span>;
      };
      $[195] = t78;
    } else {
      t78 = $[195];
    }
    const printDelegationRowDetails = t78;
    let t79;
    if ($[196] === Symbol.for("react.memo_cache_sentinel")) {
      t79 = function addToBulkRevocations(del, wallet) {
        setBulkRevocations(bd => [...bd, {
          use_case: del.useCase.use_case,
          wallet
        }]);
      };
      $[196] = t79;
    } else {
      t79 = $[196];
    }
    const addToBulkRevocations = t79;
    let t80;
    if ($[197] === Symbol.for("react.memo_cache_sentinel")) {
      t80 = function removeFromBulkRevocations(del_0, wallet_0) {
        const shouldKeepItem = (item, del_1) => !(item.use_case === del_1.useCase.use_case && areEqualAddresses(item.wallet, wallet_0));
        setBulkRevocations(bd_0 => bd_0.filter(item_0 => shouldKeepItem(item_0, del_0)));
      };
      $[197] = t80;
    } else {
      t80 = $[197];
    }
    const removeFromBulkRevocations = t80;
    let t81;
    if ($[198] !== bulkRevocations || $[199] !== chainsMatch || $[200] !== props.collection.contract) {
      t81 = function printOutgoingDelegationRow(index_1, delegations, del_2, w_0, consolidationStatus_0, pending_0, isConsolidation_0) {
        return <tr key={`outgoing-${del_2.useCase.use_case}-${index_1}-${w_0.wallet}`}><td><div className={`d-flex flex-column gap-2 ${styles.delegationAccordionBlock}`}><span className="d-flex align-items-center justify-content-between"><span className="d-flex gap-3 align-items-center">{delegations >= 2 && <FormCheck disabled={bulkRevocations.length == MAX_BULK_ACTIONS && !bulkRevocations.some(bd_1 => bd_1.use_case == del_2.useCase.use_case && areEqualAddresses(bd_1.wallet, w_0.wallet))} checked={bulkRevocations.some(bd_2 => bd_2.use_case == del_2.useCase.use_case && areEqualAddresses(bd_2.wallet, w_0.wallet))} onChange={e => {
                    if (e.target.checked) {
                      addToBulkRevocations(del_2, w_0.wallet);
                    } else {
                      removeFromBulkRevocations(del_2, w_0.wallet);
                    }
                  }} />}{printDelegationRowDetails("Incoming", w_0, consolidationStatus_0, pending_0, isConsolidation_0)}</span><span className="d-flex align-items-center gap-2"><><FontAwesomeIcon icon={faEdit} style={{
                      cursor: "pointer"
                    }} height={25} data-tooltip-id={`edit-${del_2.useCase.use_case}-${w_0.wallet}`} onClick={() => {
                      setUpdateDelegationParams({
                        wallet: w_0.wallet,
                        use_case: del_2.useCase.use_case,
                        display: del_2.useCase.display
                      });
                      setShowUpdateDelegation(true);
                      window.scrollTo(0, 0);
                    }} /><Tooltip id={`edit-${del_2.useCase.use_case}-${w_0.wallet}`} style={{
                      backgroundColor: "#1F2937",
                      color: "white",
                      padding: "4px 8px"
                    }}>Edit</Tooltip></><><FontAwesomeIcon icon={faXmark} color="white" fill="white" style={{
                      cursor: "pointer",
                      borderRadius: "25px",
                      backgroundColor: "#c51d34",
                      padding: "5px"
                    }} width={25} height={25} data-tooltip-id={`revoke-${del_2.useCase.use_case}-${w_0.wallet}`} onClick={() => {
                      let message = "Confirm in your wallet...";
                      if (chainsMatch()) {
                        setRevokeDelegationParams({
                          collection: areEqualAddresses(props.collection.contract, DELEGATION_ALL_ADDRESS) ? DELEGATION_ALL_ADDRESS : props.collection.contract,
                          address: w_0.wallet,
                          use_case: del_2.useCase.use_case
                        });
                      } else {
                        message = getSwitchToHtml();
                      }
                      setToast({
                        title: "Revoking Delegation",
                        message
                      });
                    }} /><Tooltip id={`revoke-${del_2.useCase.use_case}-${w_0.wallet}`} style={{
                      backgroundColor: "#1F2937",
                      color: "white",
                      padding: "4px 8px"
                    }}>Revoke</Tooltip></></span></span></div></td></tr>;
      };
      $[198] = bulkRevocations;
      $[199] = chainsMatch;
      $[200] = props.collection.contract;
      $[201] = t81;
    } else {
      t81 = $[201];
    }
    const printOutgoingDelegationRow = t81;
    function printOutgoingDelegations(scope, myDelegations_1) {
      let delegations_0 = 0;
      myDelegations_1.map(del_3 => {
        if (del_3.wallets.length > 0) {
          delegations_0 = delegations_0 + del_3.wallets.length;
          delegations_0;
        }
      });
      return <Container className="no-padding"><Row className={styles.delegationsTableScrollContainer}><Col className="pb-3"><Table className={styles.delegationsTable}><tbody>{delegations_0 > 0 ? myDelegations_1.map((del_4, index_2) => {
                  if (!del_4.wallets.length) {
                    return null;
                  }
                  const isConsolidation_1 = del_4.useCase.use_case === CONSOLIDATION_USE_CASE.use_case;
                  return <Fragment key={`outgoing-${del_4.useCase.use_case}-${index_2}`}><tr><td colSpan={4} className={styles.delegationsTableUseCaseHeader}>#{del_4.useCase.use_case} - {del_4.useCase.display}</td></tr>{del_4.wallets.map(w_1 => {
                      const consolidationStatus_1 = outgoingActiveConsolidations.find(i => areEqualAddresses(w_1.wallet, i.wallet))?.status;
                      const pending_1 = consolidationStatus_1 === "consolidation incomplete";
                      return printOutgoingDelegationRow(index_2, delegations_0, del_4, w_1, consolidationStatus_1, pending_1, isConsolidation_1);
                    })}</Fragment>;
                }) : !outgoingDelegationsLoaded ? <tr><td colSpan={4}>Fetching outgoing {scope}</td></tr> : <tr><td colSpan={4}>No outgoing {scope} found</td></tr>}{delegations_0 > 1 && <tr><td colSpan={4} className="pt-3">selected:{" "}{bulkRevocations.length === MAX_BULK_ACTIONS ? `${MAX_BULK_ACTIONS} (max)` : bulkRevocations.length}  <button disabled={bulkRevocations.length < 2} className={`${styles.useCaseWalletRevoke} ${bulkRevocations.length < 2 ? `${styles.useCaseWalletRevokeDisabled}` : ""}`} onClick={() => {
                      let message_0 = "Confirm in your wallet...";
                      if (chainsMatch()) {
                        setBatchRevokeDelegationParams({
                          collections: [...bulkRevocations].map(() => areEqualAddresses(props.collection.contract, DELEGATION_ALL_ADDRESS) ? DELEGATION_ALL_ADDRESS : props.collection.contract),
                          addresses: [...bulkRevocations].map(_temp19),
                          use_cases: [...bulkRevocations].map(_temp20)
                        });
                      } else {
                        message_0 = getSwitchToHtml();
                      }
                      setToast({
                        title: "Batch Revoking Delegations",
                        message: message_0
                      });
                    }}>Batch Revoke{(contractWriteBatchRevoke.isPending || waitContractWriteBatchRevoke.isLoading) && <div className="d-inline"><div className={`spinner-border ${styles.loader}`} role="status"><span className="sr-only" /></div></div>}</button></td></tr>}</tbody></Table></Col></Row></Container>;
    }
    let t82;
    if ($[202] !== subDelegationOriginalDelegator) {
      t82 = function printIncomingDelegationRow(index_3, del_5, w_2, consolidationStatus_2, pending_2, isConsolidation_2) {
        return <tr key={`incoming-${del_5.useCase.use_case}-${index_3}-${w_2.wallet}`}><td><div className={`d-flex flex-column gap-2 ${styles.delegationAccordionBlock}`}><span className="d-flex align-items-center gap-3">{del_5.useCase.use_case == SUB_DELEGATION_USE_CASE.use_case ? <FormCheck checked={areEqualAddresses(subDelegationOriginalDelegator, w_2.wallet)} onChange={e_0 => {
                  if (e_0.target.checked) {
                    setSubDelegationOriginalDelegator(w_2.wallet);
                  } else {
                    setSubDelegationOriginalDelegator(undefined);
                  }
                }} /> : <></>}{printDelegationRowDetails("Outgoing", w_2, consolidationStatus_2, pending_2, isConsolidation_2)}</span></div></td></tr>;
      };
      $[202] = subDelegationOriginalDelegator;
      $[203] = t82;
    } else {
      t82 = $[203];
    }
    const printIncomingDelegationRow = t82;
    function printIncomingDelegations(scope_0, myDelegations_2, isSubdelegation) {
      let delegations_1 = 0;
      myDelegations_2.map(del_6 => {
        if (del_6.wallets.length > 0) {
          delegations_1 = delegations_1 + del_6.wallets.length;
          delegations_1;
        }
      });
      return <Container className="no-padding"><Row className={styles.delegationsTableScrollContainer}><Col className="pb-3"><Table className={styles.delegationsTable}><tbody>{delegations_1 > 0 ? myDelegations_2.map((del_7, index_4) => {
                  if (!del_7.wallets.length) {
                    return null;
                  }
                  const isConsolidation_3 = del_7.useCase.use_case === CONSOLIDATION_USE_CASE.use_case;
                  return <Fragment key={`incoming-${del_7.useCase.use_case}-${index_4}`}><tr><td colSpan={4} className={styles.delegationsTableUseCaseHeader}>#{del_7.useCase.use_case} - {del_7.useCase.display}</td></tr>{del_7.wallets.map(w_3 => {
                      const consolidationStatus_3 = incomingActiveConsolidations.find(i_0 => areEqualAddresses(w_3.wallet, i_0.wallet))?.status;
                      const pending_3 = consolidationStatus_3 === "consolidation incomplete";
                      return printIncomingDelegationRow(index_4, del_7, w_3, consolidationStatus_3, pending_3, isConsolidation_3);
                    })}</Fragment>;
                }) : !incomingDelegationsLoaded ? <tr><td colSpan={4}>Fetching incoming {scope_0}</td></tr> : <tr><td colSpan={4}>No incoming {scope_0} found</td></tr>}{incomingDelegationsLoaded && isSubdelegation && delegations_1 > 0 && <tr><td colSpan={2} className="pt-3"><span className="d-flex flex-wrap align-items-center gap-2"><button className={`${styles.useCaseWalletUpdate} ${subDelegationOriginalDelegator === undefined ? styles.useCaseWalletUpdateDisabled : ""}`} onClick={() => {
                        setShowCreateNewDelegationWithSub(true);
                        window.scrollTo(0, 0);
                      }}><FontAwesomeIcon icon={faPlus} className={styles.buttonIcon} />Register Delegation</button><button className={`${styles.useCaseWalletUpdate} ${subDelegationOriginalDelegator === undefined ? styles.useCaseWalletUpdateDisabled : ""}`} onClick={() => {
                        setShowCreateNewSubDelegationWithSub(true);
                        window.scrollTo(0, 0);
                      }}><FontAwesomeIcon icon={faPlus} className={styles.buttonIcon} />Register Delegation Manager</button><button className={`${styles.useCaseWalletUpdate} ${subDelegationOriginalDelegator === undefined ? styles.useCaseWalletUpdateDisabled : ""}`} onClick={() => {
                        setShowCreateNewConsolidationWithSub(true);
                        window.scrollTo(0, 0);
                      }}><FontAwesomeIcon icon={faPlus} className={styles.buttonIcon} />Register Consolidation</button>{(props.collection.contract === DELEGATION_ALL_ADDRESS || props.collection.contract === MEMES_COLLECTION.contract) && <button className={`${styles.useCaseWalletUpdate} ${subDelegationOriginalDelegator === undefined ? styles.useCaseWalletUpdateDisabled : ""}`} onClick={() => {
                        setShowAssignPrimaryAddressWithSub(true);
                        window.scrollTo(0, 0);
                      }}><FontAwesomeIcon icon={faPlus} className={styles.buttonIcon} />Assign Primary Address</button>}<button className={`${styles.useCaseWalletRevoke} ${subDelegationOriginalDelegator === undefined ? styles.useCaseWalletRevokeDisabled : ""}`} onClick={() => {
                        setShowRevokeDelegationWithSub(true);
                        window.scrollTo(0, 0);
                      }}><FontAwesomeIcon icon={faMinus} className={styles.buttonIcon} />Revoke</button></span></td></tr>}</tbody></Table></Col></Row></Container>;
    }
    let t83;
    if ($[204] !== chainsMatch || $[205] !== collectionLockRead.data || $[206] !== collectionLockReadGlobal?.data || $[207] !== collectionLockWrite || $[208] !== lockUseCaseIndex || $[209] !== lockUseCaseValue || $[210] !== props.collection.contract || $[211] !== useCaseLockStatuses.data || $[212] !== useCaseLockStatusesGlobal || $[213] !== useCaseLockWrite || $[214] !== waitCollectionLockWrite.isLoading || $[215] !== waitUseCaseLockWrite.isLoading) {
      t83 = function printLocks() {
        return <Container className="no-padding"><Row className="pt-5 pb-2"><Col><h4>Locks{" "}<><FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} data-tooltip-id="locks-info" /><Tooltip id="locks-info" style={{
                    backgroundColor: "#1F2937",
                    color: "white",
                    padding: "4px 8px"
                  }}>Lock Wallet or Use Case to stop accepting incoming delegations</Tooltip></></h4></Col></Row><Row className="pt-2 pb-2"><Col><button className={`${styles.lockDelegationBtn} ${collectionLockReadGlobal?.data ? styles.lockDelegationBtnDisabled : ""}`} onClick={() => {
                const title_3 = `${collectionLockRead.data ? "Unlocking" : "Locking"} Wallet`;
                let message_1 = "Confirm in your wallet...";
                if (chainsMatch()) {
                  collectionLockWrite.writeContract({
                    address: DELEGATION_CONTRACT.contract,
                    abi: DELEGATION_ABI,
                    chainId: DELEGATION_CONTRACT.chain_id,
                    args: [props.collection.contract, !collectionLockRead.data],
                    functionName: "setCollectionLock"
                  });
                } else {
                  message_1 = getSwitchToHtml();
                }
                setToast({
                  title: title_3,
                  message: message_1
                });
              }}><FontAwesomeIcon icon={collectionLockRead.data ? faLock : faLockOpen} className={styles.buttonIcon} />{collectionLockRead.data ? "Unlock" : "Lock"} Wallet{collectionLockReadGlobal?.data && !areEqualAddresses(props.collection.contract, DELEGATION_ALL_ADDRESS) ? " *" : ""}{(collectionLockWrite.isPending || waitCollectionLockWrite.isLoading) && <Spinner />}</button></Col></Row><Row className="pt-3 pb-2"><Col xs={12} sm={12} md={4} lg={4} className="pt-2 pb-2"><Form.Select disabled={!!collectionLockRead.data} className={`${styles.formInputLockUseCase} ${collectionLockRead.data || collectionLockReadGlobal?.data ? styles.formInputDisabled : ""}`} value={lockUseCaseValue} onChange={e_1 => {
                const value = parseInt(e_1.target.value);
                setLockUseCaseValue(value);
                if (value === CONSOLIDATION_USE_CASE.use_case) {
                  setLockUseCaseIndex(18);
                } else {
                  if (value === SUB_DELEGATION_USE_CASE.use_case) {
                    setLockUseCaseIndex(17);
                  } else {
                    if (value === PRIMARY_ADDRESS_USE_CASE.use_case) {
                      setLockUseCaseIndex(16);
                    } else {
                      setLockUseCaseIndex(value - 1);
                    }
                  }
                }
                useCaseLockWrite.reset();
              }}><option value={0}>Lock/Unlock Use Case{collectionLockRead.data || collectionLockReadGlobal?.data ? " *" : ""}</option>{ALL_USE_CASES.map((uc, index_5) => {
                  if (uc.use_case === 1) {
                    return null;
                  }
                  const asteriskDisplay = useCaseLockStatusesGlobal.data?.[index_5] ? " *" : "";
                  const lockDisplay = useCaseLockStatuses.data?.[index_5] || useCaseLockStatusesGlobal.data?.[index_5] || collectionLockRead.data ? ` - LOCKED${asteriskDisplay}` : " - UNLOCKED";
                  return <option key={`collection-delegation-select-use-case-${uc.use_case}`} value={uc.use_case}>#{uc.use_case} - {uc.display}{lockDisplay}</option>;
                })}</Form.Select></Col>{lockUseCaseValue != 0 && <Col xs={12} sm={12} md={8} lg={8} className="pt-2 pb-2 d-flex align-items-center">{!useCaseLockStatusesGlobal.data || useCaseLockStatusesGlobal?.data && useCaseLockStatusesGlobal?.data[lockUseCaseIndex] as any as boolean === false ? <button className={`${styles.lockUseCaseBtn}`} onClick={() => {
                const useCase_0 = DELEGATION_USE_CASES[lockUseCaseIndex];
                const title_4 = `${useCaseLockStatuses?.data?.[lockUseCaseIndex] ? "Unlocking" : "Locking"} Wallet on Use Case #${useCase_0?.use_case} - ${useCase_0?.display}`;
                let message_2 = "Confirm in your wallet...";
                if (chainsMatch()) {
                  useCaseLockWrite.writeContract({
                    address: DELEGATION_CONTRACT.contract,
                    abi: DELEGATION_ABI,
                    chainId: DELEGATION_CONTRACT.chain_id,
                    args: [props.collection.contract, lockUseCaseValue, !useCaseLockStatuses.data?.[lockUseCaseIndex]],
                    functionName: "setCollectionUsecaseLock"
                  });
                } else {
                  message_2 = getSwitchToHtml();
                }
                setToast({
                  title: title_4,
                  message: message_2
                });
              }}><FontAwesomeIcon icon={useCaseLockStatuses.data?.[lockUseCaseIndex] ? faLock : faLockOpen} className={styles.buttonIcon} />{useCaseLockStatuses.data?.[lockUseCaseIndex] ? "Unlock" : "Lock"}{" "}Use Case{(useCaseLockWrite.isPending || waitUseCaseLockWrite.isLoading) && <Spinner />}</button> : <div><span className={styles.hint}>* Note:</span> Unlock use case in{" "}<Link href={`/delegation/${ANY_COLLECTION_PATH}`}>All Collections</Link></div>}</Col>}</Row>{collectionLockRead.data ? <Row className="pb-3"><Col><span className={styles.hint}>* Note:</span> Unlock Wallet to lock/unlock specific use cases</Col></Row> : null}{collectionLockReadGlobal?.data ? <Row className="pb-3"><Col><span className={styles.hint}>* Note:</span> Unlock Wallet on{" "}<Link href={`/delegation/${ANY_COLLECTION_PATH}`}>All Collections</Link>{" "}to lock/unlock specific collections and use cases</Col></Row> : null}</Container>;
      };
      $[204] = chainsMatch;
      $[205] = collectionLockRead.data;
      $[206] = collectionLockReadGlobal?.data;
      $[207] = collectionLockWrite;
      $[208] = lockUseCaseIndex;
      $[209] = lockUseCaseValue;
      $[210] = props.collection.contract;
      $[211] = useCaseLockStatuses.data;
      $[212] = useCaseLockStatusesGlobal;
      $[213] = useCaseLockWrite;
      $[214] = waitCollectionLockWrite.isLoading;
      $[215] = waitUseCaseLockWrite.isLoading;
      $[216] = t83;
    } else {
      t83 = $[216];
    }
    const printLocks = t83;
    T2 = Container;
    t77 = "no-padding";
    T1 = Row;
    T0 = Col;
    t76 = props.collection && <Container><Row><Col><h1 className="mb-0">{props.collection.title}</h1></Col></Row><Row className="pb-4"><Col className="d-flex align-items-center justify-content-start"><button className={styles.backBtn} onClick={() => props.setSection(DelegationCenterSection.CENTER)}><FontAwesomeIcon icon={faCircleArrowLeft} /><span className="font-smaller">Back to Delegation Center</span></button></Col></Row>{!showUpdateDelegation && !showCreateNewDelegationWithSub && !showCreateNewSubDelegationWithSub && !showCreateNewConsolidationWithSub && !showAssignPrimaryAddressWithSub && !showRevokeDelegationWithSub && <>{printDelegations()}{printConsolidations()}{printSubDelegations()}{printLocks()}<Container className="no-padding"><Row className="pt-5 pb-3"><Col className="d-flex align-items-center justify-content-start"><button className={styles.backBtn} onClick={() => props.setSection(DelegationCenterSection.CENTER)}><FontAwesomeIcon icon={faCircleArrowLeft} />Back to Delegation Center</button></Col></Row></Container></>}{showUpdateDelegation && updateDelegationParams && accountResolution.address && <UpdateDelegationComponent collection={props.collection} address={accountResolution.address} delegation={updateDelegationParams} ens={ensResolution.data} showAddMore={true} showCancel={true} onHide={() => {
        setShowUpdateDelegation(false);
      }} onSetToast={toast_0 => {
        setToast({
          title: toast_0.title,
          message: toast_0.message
        });
      }} />}{showCreateNewDelegationWithSub && subDelegationOriginalDelegator && <NewDelegationComponent subdelegation={{
        originalDelegator: subDelegationOriginalDelegator,
        collection: props.collection
      }} address={accountResolution.address as string} ens={ensResolution.data} onHide={() => {
        setShowCreateNewDelegationWithSub(false);
        setSubDelegationOriginalDelegator(undefined);
      }} onSetToast={toast_1 => {
        setToast({
          title: toast_1.title,
          message: toast_1.message
        });
      }} />}{showCreateNewSubDelegationWithSub && subDelegationOriginalDelegator && <NewSubDelegationComponent subdelegation={{
        originalDelegator: subDelegationOriginalDelegator,
        collection: props.collection
      }} address={accountResolution.address as string} ens={ensResolution.data} onHide={() => {
        setShowCreateNewSubDelegationWithSub(false);
        setSubDelegationOriginalDelegator(undefined);
      }} onSetToast={toast_2 => {
        setToast({
          title: toast_2.title,
          message: toast_2.message
        });
      }} />}{showCreateNewConsolidationWithSub && subDelegationOriginalDelegator && <NewConsolidationComponent subdelegation={{
        originalDelegator: subDelegationOriginalDelegator,
        collection: props.collection
      }} address={accountResolution.address as string} ens={ensResolution.data} onHide={() => {
        setShowCreateNewConsolidationWithSub(false);
        setSubDelegationOriginalDelegator(undefined);
      }} onSetToast={toast_3 => {
        setToast({
          title: toast_3.title,
          message: toast_3.message
        });
      }} />}{showAssignPrimaryAddressWithSub && subDelegationOriginalDelegator && <NewAssignPrimaryAddress subdelegation={{
        originalDelegator: subDelegationOriginalDelegator,
        collection: props.collection
      }} address={accountResolution.address as string} ens={ensResolution.data} onHide={() => {
        setShowAssignPrimaryAddressWithSub(false);
        setSubDelegationOriginalDelegator(undefined);
      }} onSetToast={toast_4 => {
        setToast({
          title: toast_4.title,
          message: toast_4.message
        });
      }} />}{showRevokeDelegationWithSub && subDelegationOriginalDelegator && accountResolution.address && <RevokeDelegationWithSubComponent originalDelegator={subDelegationOriginalDelegator} collection={props.collection} address={accountResolution.address} ens={ensResolution.data} showAddMore={true} onHide={() => {
        setShowRevokeDelegationWithSub(false);
        setSubDelegationOriginalDelegator(undefined);
      }} onSetToast={toast_5 => {
        setToast({
          title: toast_5.title,
          message: toast_5.message
        });
      }} />}</Container>;
    $[156] = accountResolution.address;
    $[157] = bulkRevocations;
    $[158] = chainsMatch;
    $[159] = collectionLockRead.data;
    $[160] = collectionLockReadGlobal?.data;
    $[161] = collectionLockWrite;
    $[162] = consolidationKeys;
    $[163] = contractWriteBatchRevoke.isPending;
    $[164] = delegationKeys;
    $[165] = ensResolution;
    $[166] = incomingActiveConsolidations;
    $[167] = incomingDelegations;
    $[168] = incomingDelegationsLoaded;
    $[169] = lockUseCaseIndex;
    $[170] = lockUseCaseValue;
    $[171] = outgoingActiveConsolidations;
    $[172] = outgoingDelegations;
    $[173] = outgoingDelegationsLoaded;
    $[174] = props;
    $[175] = showAssignPrimaryAddressWithSub;
    $[176] = showCreateNewConsolidationWithSub;
    $[177] = showCreateNewDelegationWithSub;
    $[178] = showCreateNewSubDelegationWithSub;
    $[179] = showRevokeDelegationWithSub;
    $[180] = showUpdateDelegation;
    $[181] = subDelegationKeys;
    $[182] = subDelegationOriginalDelegator;
    $[183] = updateDelegationParams;
    $[184] = useCaseLockStatuses.data;
    $[185] = useCaseLockStatusesGlobal;
    $[186] = useCaseLockWrite;
    $[187] = waitCollectionLockWrite.isLoading;
    $[188] = waitContractWriteBatchRevoke.isLoading;
    $[189] = waitUseCaseLockWrite.isLoading;
    $[190] = T0;
    $[191] = T1;
    $[192] = T2;
    $[193] = t76;
    $[194] = t77;
  } else {
    T0 = $[190];
    T1 = $[191];
    T2 = $[192];
    t76 = $[193];
    t77 = $[194];
  }
  let t78;
  if ($[217] !== T0 || $[218] !== t76) {
    t78 = <T0>{t76}</T0>;
    $[217] = T0;
    $[218] = t76;
    $[219] = t78;
  } else {
    t78 = $[219];
  }
  let t79;
  if ($[220] !== T1 || $[221] !== t78) {
    t79 = <T1>{t78}</T1>;
    $[220] = T1;
    $[221] = t78;
    $[222] = t79;
  } else {
    t79 = $[222];
  }
  let t80;
  if ($[223] !== showToast || $[224] !== toast) {
    t80 = toast && <DelegationToast toastRef={toastRef} toast={toast} showToast={showToast} setShowToast={setShowToast} />;
    $[223] = showToast;
    $[224] = toast;
    $[225] = t80;
  } else {
    t80 = $[225];
  }
  let t81;
  if ($[226] !== T2 || $[227] !== t77 || $[228] !== t79 || $[229] !== t80) {
    t81 = <T2 className={t77}>{t79}{t80}</T2>;
    $[226] = T2;
    $[227] = t77;
    $[228] = t79;
    $[229] = t80;
    $[230] = t81;
  } else {
    t81 = $[230];
  }
  return t81;
}
function _temp20(br_0) {
  return br_0.use_case;
}
function _temp19(br) {
  return br.wallet;
}
function _temp18(d_10) {
  return d_10.useCase.use_case === CONSOLIDATION_USE_CASE.use_case;
}
function _temp17(k_4) {
  return k_4 != "1";
}
function _temp16(d_9) {
  return d_9.useCase.use_case === CONSOLIDATION_USE_CASE.use_case;
}
function _temp15(k_3) {
  return k_3 != "0";
}
function _temp14(d_8) {
  return d_8.useCase.use_case === SUB_DELEGATION_USE_CASE.use_case;
}
function _temp13(k_2) {
  return k_2 != "1";
}
function _temp12(d_7) {
  return d_7.useCase.use_case === SUB_DELEGATION_USE_CASE.use_case;
}
function _temp11(k_1) {
  return k_1 != "0";
}
function _temp10(k_0) {
  return k_0 != "1";
}
function _temp9(k) {
  return k != "0";
}
function _temp8(d_6) {
  return d_6.useCase.use_case != SUB_DELEGATION_USE_CASE.use_case && d_6.useCase.use_case != CONSOLIDATION_USE_CASE.use_case;
}
function _temp7(d_5) {
  return d_5.useCase.use_case != SUB_DELEGATION_USE_CASE.use_case && d_5.useCase.use_case != CONSOLIDATION_USE_CASE.use_case;
}
function _temp6(d_4) {
  return d_4.useCase.use_case === CONSOLIDATION_USE_CASE.use_case;
}
function _temp5(d_3) {
  return d_3.useCase.use_case === CONSOLIDATION_USE_CASE.use_case;
}
function _temp4(d_2) {
  return d_2.useCase.use_case === SUB_DELEGATION_USE_CASE.use_case;
}
function _temp3(d_1) {
  return d_1.useCase.use_case === SUB_DELEGATION_USE_CASE.use_case;
}
function _temp2(d_0) {
  return d_0.useCase.use_case != SUB_DELEGATION_USE_CASE.use_case && d_0.useCase.use_case != CONSOLIDATION_USE_CASE.use_case;
}
function _temp(d) {
  return d.useCase.use_case != SUB_DELEGATION_USE_CASE.use_case && d.useCase.use_case != CONSOLIDATION_USE_CASE.use_case;
}